import { Button, Dropdown, Flex, Input, Select, Space, TableProps } from "antd";
import { useEffect, useState } from "react";
import ActionIcon from "../../../assets/icons/ActionIcon";
import SearchIcon from "../../../assets/icons/SearchIcon";
import ViewIcon from "../../../assets/icons/ViewIcon";
import TitleBar from "../../../components/common/TitleBar";
import CustomTable from "../../../components/ui/CustomTable";
import {
  useGetInvoiceListQuery,
  useSendToDisbursementMutation,
} from "../../../features/Invoice/invoice.api";
import { InvoiceListDetails } from "../../../features/Invoice/invoice.type";
import { useGetVendorListQuery } from "../../../features/Vendor/vendor.api";
import useError from "../../../hooks/useError";
import useModal from "../../../hooks/useModal";
import useTable from "../../../hooks/useTable";
import SidebarLayout from "../../../layouts/SidebarLayout";
import { toast } from "../../../lib/toast";
import {
  addCommaInNumber,
  countParPageSerialNumber,
} from "../../../utils/number";
import { capitalize, valueOrNa } from "../../../utils/string";
import {
  addKeyInArray,
  selectArrayOption,
  selectSearchOption,
} from "../../../utils/utils";
import InvoiceDetails from "./InvoiceDetails";
import { INVOICE_STATUS_CONFIG } from "./InvoiceList";

type TFilter = {
  vendor_id?: string;
  status?: string;
};

type TModalData = { id: number; title: string };

const WaitingForPaymentList = () => {
  const { open, handleOpen, handleClose } = useModal<TModalData, "ARCHIVE">();

  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);

  const {
    page,
    limit,
    filter,
    keyword,
    handleFilter,
    handleLimit,
    handlePagination,
    handleSearch,
  } = useTable<TFilter>({});

  const {
    data: invoiceList,
    isLoading,
    isFetching,
  } = useGetInvoiceListQuery({
    params: {
      ...filter,
      page,
      limit,
      status: "pending_for_disbursement",
      search: keyword,
    },
  });
  const { data: vendorList } = useGetVendorListQuery({
    params: { status: "active" },
  });

  // ------ Mutations ------
  const [
    sendToDisbursement,
    {
      isLoading: sending,
      isSuccess: sent,
      isError: isSendError,
      error: sendError,
    },
  ] = useSendToDisbursementMutation();

  // ------ Calculate total amount ------
  const totalSelectedAmount = invoiceList?.data?.invoices
    .filter((invoice) => selectedRowKeys.includes(invoice.id))
    .reduce((sum, invoice) => sum + Number(invoice.invoice_amount), 0);

  // ------ Function for send to disbursement ------
  const handleSendToDisbursement = () => {
    sendToDisbursement({ data: { invoice_ids: selectedRowKeys } });
  };

  useEffect(() => {
    if (sent) {
      toast("success", "Invoice sent to disbursement.");
    }
  }, [sent]);

  useError(isSendError, sendError, "Invoice send to disbursement failed.");

  const columns: TableProps<InvoiceListDetails>["columns"] = [
    {
      title: "#SL",
      align: "center",
      width: 60,
      render: (_, __, index) => countParPageSerialNumber(page, limit, index),
    },
    {
      title: "Invoice Id",
      dataIndex: "invoice_id",
      render: (val) => valueOrNa(val),
    },
    {
      title: "Job Title",
      dataIndex: "job_title",
      render: (val) => valueOrNa(val),
    },
    {
      title: "Vendor",
      dataIndex: "vendor",
      render: (val) => valueOrNa(val?.business_name),
    },
    {
      title: "Reference ID",
      dataIndex: "reference_id",
      render: (val) => valueOrNa(val),
    },
    {
      title: "Invoice Amount [BDT]",
      dataIndex: "invoice_amount",
      render: (val) => `BDT ${addCommaInNumber(val)}`,
    },
    {
      title: "Approver Status",
      dataIndex: "status",
      align: "center",
      render: (val) => {
        return (
          <span
            className={
              INVOICE_STATUS_CONFIG[
                String(
                  val as string,
                ).toLowerCase() as keyof typeof INVOICE_STATUS_CONFIG
              ]
            }
          >
            {capitalize(val?.split("_").join(" "))}
          </span>
        );
      },
    },
    {
      title: "Action",
      align: "center",
      width: 130,
      render: (invoice) => (
        <Space align="center">
          <Dropdown
            menu={{
              items: [
                {
                  key: "view",
                  label: (
                    <Space>
                      <ViewIcon />
                      <span>View</span>
                    </Space>
                  ),
                  onClick: () =>
                    handleOpen({
                      type: "VIEW",
                      data: {
                        id: invoice.id,
                        title: `${invoice.invoice_id} ${invoice.name}`,
                      },
                    }),
                },
              ],
            }}
            placement="bottomRight"
            trigger={["click"]}
            overlayClassName="w-[180px]"
          >
            <Button className="size-8 p-0 border-none shadow-none">
              <ActionIcon />
            </Button>
          </Dropdown>
        </Space>
      ),
    },
  ];
  return (
    <SidebarLayout title="Payable Management">
      <TitleBar
        title="Waiting for payment"
        subTitle="Manage vendor payment invoice"
      >
        <Button
          disabled={!selectedRowKeys?.length}
          loading={sending}
          onClick={handleSendToDisbursement}
          className="button-primary"
        >
          Submit for Disbursement
        </Button>
      </TitleBar>

      <div className="bg-theme-primaryRest p-4 rounded-lg mb-6 space-y-2">
        <Flex align="center" justify="space-between">
          <h4 className="text-lg font-semibold text-theme-gray-2">
            Total Amount :
          </h4>
          <h4 className="text-lg font-semibold text-theme-primary">
            BDT {totalSelectedAmount}
          </h4>
        </Flex>
        <Flex align="center" justify="space-between">
          <h5 className="text-base font-medium text-theme-gray-50">
            Selected Payable :
          </h5>
          <h5 className="text-base font-medium text-theme-gray-2">
            {selectedRowKeys?.length} Invoice
          </h5>
        </Flex>
      </div>

      <CustomTable
        title={"Invoices List"}
        loading={isLoading || isFetching}
        limit={limit}
        page={page}
        columns={columns}
        total={invoiceList?.data.totalLength}
        dataSource={addKeyInArray(invoiceList?.data.invoices || [])}
        handleLimit={handleLimit}
        handlePagination={handlePagination}
        hideDifferentRowColor
        rowSelection={{
          selectedRowKeys,
          onChange: (newSelectedRowKeys: React.Key[]) => {
            setSelectedRowKeys(newSelectedRowKeys);
          },
        }}
        filterSection={
          <>
            <Input
              className="bg-input"
              suffix={<SearchIcon />}
              placeholder="Search name, id"
              style={{ minWidth: 170, maxWidth: 180 }}
              onChange={(val) => handleSearch(val)}
            />
            <Select
              allowClear
              showSearch
              placeholder="Vendors"
              filterOption={selectSearchOption}
              options={selectArrayOption(
                vendorList?.data?.vendors || [],
                "business_name",
                "id",
              )}
              className="min-w-[90px] bg-select"
              rootClassName="icon-gray"
              onChange={(val) => handleFilter("vendor_id", val)}
              popupMatchSelectWidth={150}
              placement="bottomRight"
            />
            {/* <Select
              allowClear
              placeholder="Status"
              options={[
                {
                  label: "Pending For Disbursement",
                  value: "pending_for_disbursement",
                },
              ]}
              disabled
              value={"pending_for_disbursement"}
              placement="bottomRight"
              className="bg-select"
              rootClassName="icon-gray"
              onChange={(val) => handleFilter("status", val)}
              popupMatchSelectWidth={220}
            /> */}
          </>
        }
      />

      <InvoiceDetails
        open={open.open && open.type === "VIEW"}
        handleClose={handleClose}
        data={open.data?.id}
        type="waiting-for-payment"
      />
    </SidebarLayout>
  );
};

export default WaitingForPaymentList;
